import {
  SupplierRelationshipListNewParameterModifierType,
  SupplierRelationshipListNewSortModifierType,
} from "@sellar-io/api-v2/types";
import { FilterType, SortType } from "../../../lists/types";

export type CustomerListSearchType = "name" | "postalCode";

export const CustomerListSearchValues: FilterType<
  SupplierRelationshipListNewParameterModifierType,
  CustomerListSearchType
> = {
  name: {
    key: "name",
    name: "Name",
    mutation: (value: string) => [
      {
        type: "name",
        modifier: "INCLUDES",
        value,
      },
    ],
  },
  postalCode: {
    key: "postalCode",
    name: "Postcode",
    mutation: (value: string) => [
      {
        type: "postalCode",
        modifier: "INCLUDES",
        value,
      },
    ],
  },
};

export type CustomerPaymentFilterType =
  | "customers"
  | "pending"
  | "connected"
  // | "requestedPaymentTerms"
  | "proforma"
  | "credit"
  | "paymentOnDelivery";

export const CustomerPaymentFilterValues: FilterType<
  SupplierRelationshipListNewParameterModifierType,
  CustomerPaymentFilterType
> = {
  customers: {
    key: "customers",
    name: "All payment terms",
    mutation: () => [],
  },
  pending: {
    key: "pending",
    name: "Awaiting terms",
    mutation: () =>
      [
        {
          type: "approved",
          modifier: "EQUALS",
          value: false,
        },
      ] as const,
  },
  connected: {
    key: "connected",
    name: "Terms assigned",
    mutation: () =>
      [
        {
          type: "approved",
          modifier: "EQUALS",
          value: true,
        },
      ] as const,
  },
  proforma: {
    key: "proforma",
    name: "Pro-forma",
    mutation: () =>
      [
        {
          type: "paymentTerms",
          modifier: "EQUALS",
          value: "proforma",
        },
      ] as const,
  },
  paymentOnDelivery: {
    key: "paymentOnDelivery",
    name: "Pay on delivery",
    mutation: () =>
      [
        {
          type: "paymentTerms",
          modifier: "EQUALS",
          value: "paymentOnDelivery",
        },
      ] as const,
  },
  // requestedPaymentTerms: {
  //   key: "requestedPaymentTerms",
  //   name: "Terms requested",
  //   mutation: () =>
  //     [
  //       {
  //         type: "requestedPaymentTerms",
  //         modifier: "EQUALS",
  //         value: true,
  //       },
  //     ] as const,
  // },
  credit: {
    key: "credit",
    name: "Credit",
    mutation: () =>
      [
        {
          type: "paymentTerms",
          modifier: "EQUALS",
          value: "credit",
        },
      ] as const,
  },
};

export type CustomerDeliveryFilterType =
  | "all"
  | "direct"
  | "sellarDistribution"
  | "collection"
  | "courier"
  | "haulier";

export const CustomerDeliveryFilterValues: FilterType<
  SupplierRelationshipListNewParameterModifierType,
  CustomerDeliveryFilterType
> = {
  all: {
    key: "all",
    name: "All delivery tiers",
    mutation: () => [],
  },
  direct: {
    key: "direct",
    name: "Direct",
    mutation: () =>
      [
        {
          type: "fulfillmentType",
          modifier: "EQUALS",
          value: "direct",
        },
      ] as const,
  },
  sellarDistribution: {
    key: "sellarDistribution",
    name: "Sellar Distribution",
    mutation: () =>
      [
        {
          type: "fulfillmentType",
          modifier: "EQUALS",
          value: "sellarDistribution",
        },
      ] as const,
  },
  collection: {
    key: "collection",
    name: "Collection",
    mutation: () =>
      [
        {
          type: "fulfillmentType",
          modifier: "EQUALS",
          value: "collection",
        },
      ] as const,
  },
  courier: {
    key: "courier",
    name: "Courier",
    mutation: () =>
      [
        {
          type: "fulfillmentType",
          modifier: "EQUALS",
          value: "courier",
        },
      ] as const,
  },
  haulier: {
    key: "haulier",
    name: "Haulier",
    mutation: () =>
      [
        {
          type: "fulfillmentType",
          modifier: "EQUALS",
          value: "haulier",
        },
      ] as const,
  },
};

export type CustomerListSortType =
  | "name_asc"
  | "name_desc"
  | "last_ordered_asc"
  | "last_ordered_desc"
  | "average_order_value_desc"
  | "average_order_value_asc";

export const CustomerListSortValues: SortType<
  SupplierRelationshipListNewSortModifierType,
  CustomerListSortType
> = {
  name_asc: {
    key: "name_asc",
    name: "Customer name (A-Z)",
    mutation: {
      type: "name",
      direction: "asc",
    },
  },
  name_desc: {
    key: "name_desc",
    name: "Customer name (Z-A)",
    mutation: {
      type: "name",
      direction: "desc",
    },
  },
  last_ordered_desc: {
    key: "last_ordered_desc",
    name: "Last ordered (Newest - Oldest)",
    mutation: {
      type: "lastOrderedAt",
      direction: "desc",
    },
  },
  last_ordered_asc: {
    key: "last_ordered_asc",
    name: "Last ordered (Oldest - Newest)",
    mutation: {
      type: "lastOrderedAt",
      direction: "asc",
    },
  },
  average_order_value_asc: {
    key: "average_order_value_asc",
    name: "Average order size (Largest - Smallest)",
    mutation: {
      type: "averageOrderValue",
      direction: "asc",
    },
  },
  average_order_value_desc: {
    key: "average_order_value_desc",
    name: "Average order size (Smallest - Largest)",
    mutation: {
      type: "averageOrderValue",
      direction: "desc",
    },
  },
};

export type AcquiredViaFilterType = "all_channels" | "market";

export const AcquiredViaFilterValues: FilterType<
  SupplierRelationshipListNewParameterModifierType,
  AcquiredViaFilterType
> = {
  all_channels: {
    key: "all_channels",
    name: "All channels",
    mutation: () => [],
  },
  market: {
    key: "market",
    name: "Sellar market",
    mutation: () =>
      [
        {
          type: "hasOpeningOrderFee",
          modifier: "EQUALS",
          value: true,
        },
      ] as const,
  },
};

export type DateUnits = "days" | "weeks" | "months" | "years";
